/**
 * IFRAP-AI.org - Page Suivi des Coûts LLM
 * Visualisation des dépenses d'utilisation de l'IA
 * Détection des doublons et optimisation des coûts
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { DollarSign, TrendingUp, Clock, RefreshCw, PieChart, BarChart2, AlertTriangle, Copy } from 'lucide-react';

const CostsPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/costs/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Error fetching cost data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  const totals = status?.totals || {};
  const byOperation = status?.by_operation || {};
  const daily = status?.daily || {};
  const recent = status?.recent_records || [];
  const apiKeys = status?.api_keys || {};
  const duplicates = status?.duplicates || {};
  const pricing = status?.pricing || {};

  return (
    <div className="space-y-6" data-testid="costs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Suivi des Coûts LLM</h1>
          <p className="text-gray-400 text-sm mt-1">Comptabilisation des tokens et dépenses IA en euros</p>
        </div>
        <button
          onClick={fetchData}
          data-testid="refresh-costs-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Totaux principaux */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Coût Total</span>
            <DollarSign className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-2xl font-mono text-white" data-testid="total-cost-eur">{totals.total_cost_eur?.toFixed(4) || '0.0000'}€</p>
          <p className="text-xs text-gray-500 mt-1">${totals.total_cost_usd?.toFixed(4) || '0.0000'} USD</p>
        </div>

        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Tokens Utilisés</span>
            <TrendingUp className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-2xl font-mono text-white">{(totals.total_tokens || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">
            {(totals.total_input_tokens || 0).toLocaleString()} in / {(totals.total_output_tokens || 0).toLocaleString()} out
          </p>
        </div>

        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Opérations</span>
            <PieChart className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-2xl font-mono text-white">{totals.total_operations || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Requêtes LLM exécutées</p>
        </div>

        {/* Doublons détectés */}
        <div className={`bg-[#1a1a2e] border rounded-lg p-4 ${totals.duplicates_detected > 0 ? 'border-orange-500/50' : 'border-gray-800'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Doublons</span>
            <Copy className={`w-5 h-5 ${totals.duplicates_detected > 0 ? 'text-orange-400' : 'text-gray-600'}`} />
          </div>
          <p className={`text-2xl font-mono ${totals.duplicates_detected > 0 ? 'text-orange-400' : 'text-white'}`}>
            {totals.duplicates_detected || 0}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totals.duplicate_cost_wasted > 0 
              ? `${totals.duplicate_cost_wasted.toFixed(4)}€ gaspillés` 
              : 'Aucun gaspillage'}
          </p>
        </div>

        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Clé API</span>
            <div className={`w-2 h-2 rounded-full ${apiKeys.emergent_key_configured ? 'bg-green-500' : 'bg-red-500'}`} />
          </div>
          <p className="text-lg font-mono text-white">
            {apiKeys.active_provider === 'emergent' ? 'Emergent' : 'Custom'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {apiKeys.emergent_key_configured ? 'Configurée' : 'Non configurée'}
          </p>
        </div>
      </div>

      {/* Alerte doublons si nécessaire */}
      {totals.duplicates_detected > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
            <div>
              <p className="text-orange-400 font-medium">Requêtes dupliquées détectées</p>
              <p className="text-gray-400 text-sm mt-1">
                {duplicates.recommendation || "Certaines requêtes ont été répétées inutilement. Vérifiez vos workflows."}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par opération */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5" />
            Par type d'opération
          </h2>

          {Object.keys(byOperation).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(byOperation).map(([op, data]) => {
                const percentage = totals.total_cost_eur > 0 
                  ? (data.cost_eur / totals.total_cost_eur * 100) 
                  : 0;
                
                const colors = {
                  scan: 'bg-blue-500',
                  summary: 'bg-green-500',
                  report: 'bg-purple-500',
                  press_review: 'bg-orange-500',
                  analysis: 'bg-pink-500',
                  revue_presse: 'bg-yellow-500',
                };

                return (
                  <div key={op} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300 capitalize">{op.replace('_', ' ')}</span>
                      <span className="text-gray-400">
                        {data.count} ops · {data.cost_eur?.toFixed(4)}€
                      </span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[op] || 'bg-gray-500'} transition-all`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Aucune opération enregistrée</p>
          )}
        </div>

        {/* Dernières opérations */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Dernières opérations
          </h2>

          {recent.length > 0 ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {recent.map((record, idx) => (
                <div 
                  key={record.id || idx}
                  className={`flex items-center justify-between py-2 border-b last:border-0 ${
                    record.is_duplicate 
                      ? 'border-orange-500/30 bg-orange-500/5' 
                      : 'border-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {record.is_duplicate && (
                      <Copy className="w-3 h-3 text-orange-400" title="Requête dupliquée" />
                    )}
                    <div>
                      <p className="text-sm text-gray-300 capitalize">
                        {record.operation}
                        {record.is_duplicate && <span className="text-orange-400 text-xs ml-2">(doublon)</span>}
                      </p>
                      <p className="text-xs text-gray-500">{record.tokens} tokens · {record.model}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${record.is_duplicate ? 'text-orange-400' : 'text-[#00FF88]'}`}>
                      {record.cost_eur?.toFixed(4)}€
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(record.timestamp).toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Aucune opération récente</p>
          )}
        </div>
      </div>

      {/* Tarification multi-modèles */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-mono text-gray-400 mb-4">Grille tarifaire des modèles</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
          {pricing.models && Object.entries(pricing.models).map(([model, prices]) => (
            <div key={model} className="bg-black/30 rounded p-3">
              <p className="text-[#00FF88] font-mono mb-2">{model}</p>
              <p className="text-gray-500">Input: <span className="text-white">{prices.input}</span></p>
              <p className="text-gray-500">Output: <span className="text-white">{prices.output}</span></p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Taux de change: {pricing.exchange_rate} · Seuil d'alerte: {status?.alert_threshold_eur}€/opération
        </p>
      </div>
    </div>
  );
};

export default CostsPage;
